import { saveEmailSignature } from '@api/email-signature-api';
import { createImageProxyLinksForImageBasedSignature } from '@modules/email-signatures-helpers';
import { generateEntityId } from '@modules/helpers';
import { createAnonymousUser } from '@modules/user-helpers';
import { initialEmailSignatureContextValues } from '@reducers/email-signature-reducer';
import firebase from 'firebase';
import { CountryCode } from 'libphonenumber-js';
import { keyBy } from 'lodash';
import { AnonymousUser, User } from 'types';
import { EmailSignatureContactDetailsFieldWidth } from '../consts';
import {
  EmailSignaturePersonalDetailsFields,
  EmailSignatureContactDetailsField,
  EmailSignatureContactDetailsFieldTypes,
  EmailSignatureSocialLink,
  EmailSignatureSocialLinkTypes,
  EmailSignaturesImages,
  EmailSignatureShapes,
  EmailSignatureSizes,
  EmailSignatureV2,
} from '../types';
import { NextRouter } from 'next/router';
import { analytics } from '@modules/analytics';
import { AnalyticsSignatureEmailClientType } from '.cache/__types__';
import { createUserDoc, updateUserDoc } from '@modules/user-doc-helpers';

function getMemorisedOnboardingEmailSignatureId(userId: string): string | null {
  return window.localStorage.getItem(`BLINQ_ONBOARDING_SIGNATURE_ID_USER_${userId}`);
}

function memoriseOnboardingEmailSignatureId(userId: string, emailSingatureId: string): void {
  window.localStorage.setItem(`BLINQ_ONBOARDING_SIGNATURE_ID_USER_${userId}`, emailSingatureId);
}

function memoriesOnboardingSignupUserId(userId: string): void {
  window.localStorage.setItem(`BLINQ_ONBOARDING_SIGN_UP_USER_ID`, userId);
}

function getMemorisedOnboardingSignupUserId(): string | null {
  const value = window.localStorage.getItem(`BLINQ_ONBOARDING_SIGN_UP_USER_ID`);
  window.localStorage.removeItem(`BLINQ_ONBOARDING_SIGN_UP_USER_ID`);

  return value;
}

function memoriesOnboardingSignupSignatureId(emailSignatureId: string): void {
  window.localStorage.setItem(`BLINQ_ONBOARDING_SIGN_UP_SIGNATURE_ID`, emailSignatureId);
}

function getMemorisedOnboardingSignupSignatureId(): string | null {
  const value = window.localStorage.getItem(`BLINQ_ONBOARDING_SIGN_UP_SIGNATURE_ID`);
  window.localStorage.removeItem(`BLINQ_ONBOARDING_SIGN_UP_SIGNATURE_ID`);

  return value;
}

function composeExamplePersonalDetails(): EmailSignaturePersonalDetailsFields {
  return {
    firstName: 'Alex',
    lastName: 'Garcia',
    jobTitle: 'Lead Designer',
    company: 'Assemblr Digital Agency',
    headline: '',
  };
}

function composeExampleContactDetails(): EmailSignatureContactDetailsField[] {
  const exampleContactDetails = [
    {
      baseIcon: 'phone-alt',
      iconUrl: '/img/email-signature-example/example-phone-icon.png',
      value: '+1(201) 555-5555',
      label: '',
      id: 'ct_1',
      type: EmailSignatureContactDetailsFieldTypes.PHONE,
      fieldWidth: EmailSignatureContactDetailsFieldWidth.HALF_WIDTH,
    },
    {
      baseIcon: 'envelope',
      iconUrl: '/img/email-signature-example/example-mail-icon.png',
      value: 'johndoe@gmail.com',
      label: '',
      id: 'ct_2',
      type: EmailSignatureContactDetailsFieldTypes.EMAIL,
      fieldWidth: EmailSignatureContactDetailsFieldWidth.HALF_WIDTH,
    },
    {
      baseIcon: 'browser',
      iconUrl: '/img/email-signature-example/example-browser-icon.png',
      value: 'http://assemblr.com.au/',
      title: 'assemblr.com.au',
      label: '',
      id: 'ct_3',
      type: EmailSignatureContactDetailsFieldTypes.WEBSITE,
      fieldWidth: EmailSignatureContactDetailsFieldWidth.HALF_WIDTH,
    },
  ];

  // the "as EmailSignatureContactDetailsField[]" is to fix typescript's bug where it does not recognise ct_3 as ct_${string}
  return exampleContactDetails as EmailSignatureContactDetailsField[];
}

function composeExampleSocialLinks(): EmailSignatureSocialLink[] {
  const exampleSocialLinks = [
    {
      type: EmailSignatureSocialLinkTypes.FACEBOOK,
      iconUrl: '/img/email-signature-example/example-facebook-icon.png',
      id: 'sc_1',
      value: 'fakeValue',
    },
    {
      type: EmailSignatureSocialLinkTypes.LINKEDIN,
      iconUrl: '/img/email-signature-example/example-linkedin-icon.png',
      id: 'sc_2',
      value: 'fakeValue',
    },
    {
      type: EmailSignatureSocialLinkTypes.INSTAGRAM,
      iconUrl: '/img/email-signature-example/example-instagram-icon.png',
      id: 'sc_3',
      value: 'fakeValue',
    },
  ];

  // the "as EmailSignatureContactDetailsField[]" is to fix typescript's bug where it does not recognise sc_3 as sc_${string}
  return exampleSocialLinks as EmailSignatureSocialLink[];
}

function composeExampleImages(): EmailSignaturesImages {
  const exampleImages = [
    {
      id: 'im_1',
      externalLink: '',
      imageUrl: '/img/email-signature-example/example-profile-image.png',
      shape: EmailSignatureShapes.CIRCLE,
      size: EmailSignatureSizes.MEDIUM,
      type: 'profile-picture',
      isPrimary: true,
      originalImageUrl: '',
      crop: null,
    },
    {
      id: 'im_2',
      externalLink: '',
      imageUrl: '/img/email-signature-example/example-company-logo.png',
      shape: EmailSignatureShapes.SQUIRCLE,
      size: EmailSignatureSizes.MEDIUM,
      type: 'company-logo',
      isPrimary: false,
      originalImageUrl: '',
      crop: null,
    },
  ];

  return exampleImages as EmailSignaturesImages;
}

function composeExampleEmailSignature(): EmailSignatureV2 {
  const examplePersonalDetails = composeExamplePersonalDetails();

  const exampleContactDetails = composeExampleContactDetails();
  const contactFieldsOrder = exampleContactDetails.map((contactField) => contactField.id);

  const exampleSocialLinks = composeExampleSocialLinks();
  const socialLinksOrder = exampleSocialLinks.map((socialLink) => socialLink.id);

  const exampleImages = composeExampleImages();

  return {
    ...initialEmailSignatureContextValues,
    personalDetails: examplePersonalDetails,
    contactDetails: keyBy(exampleContactDetails, 'id'),
    contactDetailsOrder: contactFieldsOrder,
    socialLinks: keyBy(exampleSocialLinks, 'id'),
    socialLinksOrder: socialLinksOrder,
    images: exampleImages,
  };
}

/**
 * Get guessed country code
 */
async function getGeo(): Promise<CountryCode | undefined> {
  const response = await fetch('https://ipinfo.io', {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
  const data = await response.json();

  return data.country;
}

/**
 * Create a new user and email signature objects
 * @param firebaseUser the temporary anonymous user
 * @param localEmailSignatureWithStorageImagesUrls the local email signature with storage links
 * @param countryGuess The guessed country
 * @returns new the email signature and card
 */
async function createNewUserFromEmailSignature(
  firebaseUser: firebase.User,
  localEmailSignature: EmailSignatureV2
): Promise<AnonymousUser> {
  const newAnonymousUser: AnonymousUser = createAnonymousUser(firebaseUser, {
    firstName: localEmailSignature.personalDetails.firstName,
    lastName: localEmailSignature.personalDetails.lastName,
    userId: firebaseUser.uid,
  });

  await createUserDoc(newAnonymousUser);

  return newAnonymousUser;
}

/**
 * Create a new user and email signature objects
 * @param firebaseUser the temporary anonymous user
 * @param localEmailSignatureWithStorageImagesUrls the local email signature with storage links
 * @param countryGuess The guessed country
 * @returns new the email signature and card
 */
async function updateUserFromEmailSignature(
  anonymousUser: AnonymousUser,
  localEmailSignature: EmailSignatureV2
): Promise<AnonymousUser> {
  const updatedUser: AnonymousUser = {
    ...anonymousUser,
    firstName: localEmailSignature.personalDetails.firstName,
    lastName: localEmailSignature.personalDetails.lastName,
  };

  await updateUserDoc(updatedUser);

  return updatedUser;
}

/**
 * Create a new user and email signature objects
 * @param firebaseUser the temporary anonymous user
 * @param localEmailSignatureWithStorageImagesUrls the local email signature with storage links
 * @param countryGuess The guessed country
 * @returns new the email signature and card
 */
async function createNewEmailSignature(
  firebaseUser: firebase.User,
  localEmailSignature: EmailSignatureV2
): Promise<EmailSignatureV2> {
  const newEmailSignatureToCreate: EmailSignatureV2 = {
    ...localEmailSignature,
    title: 'Email Signature #1',
    createdBy: {
      id: firebaseUser.uid,
      firstName: localEmailSignature.personalDetails.firstName ?? '',
      lastName: localEmailSignature.personalDetails.lastName ?? '',
    },
    saveContact: {
      ...localEmailSignature.saveContact,
      cardId: generateEntityId(),
    },
    userId: firebaseUser.uid,
    id: generateEntityId(32),
    updatedAt: firebase.firestore.Timestamp.fromMillis(Date.now()),
    createdAt: firebase.firestore.Timestamp.fromMillis(Date.now()),
  };

  await saveEmailSignature(newEmailSignatureToCreate);
  await createImageProxyLinksForImageBasedSignature(newEmailSignatureToCreate);

  return newEmailSignatureToCreate;
}

/**
 * Update the user object and email signature object
 * @param anonymousUser the anonymous user object
 * @param localEmailSignatureWithStorageImagesUrls the local email signature with storage links
 * @param countryGuess The guessed country
 * @returns new the email signature and card
 */
async function updateEmailSignature(
  localEmailSignatureWithStorageImagesUrls: EmailSignatureV2
): Promise<EmailSignatureV2> {
  const updatedSignature = {
    ...localEmailSignatureWithStorageImagesUrls,
    updatedAt: firebase.firestore.Timestamp.fromMillis(Date.now()),
  };

  await saveEmailSignature(updatedSignature);
  await createImageProxyLinksForImageBasedSignature(updatedSignature);

  return updatedSignature;
}

async function updateUserDetailsUponSigningUp(currentUser: User): Promise<void> {
  const firebaseUser = firebase.auth().currentUser;

  if (!firebaseUser) {
    return;
  }

  const updatedUserDetails = {
    ...currentUser,
    signInEmailAddress: firebaseUser.email || currentUser?.signInEmailAddress,
    isAnonymous: false,
  };

  await updateUserDoc(updatedUserDetails);
  if (!firebaseUser.emailVerified) {
    firebaseUser.sendEmailVerification();
  }
}

async function resetWhenSomethingGoneWrong(router: NextRouter): Promise<void> {
  // reload on the next cycle, if reload immediately next will redirect to the current page instead of /onboarding/email-signature/details for some reason
  setTimeout(() => router.reload(), 0);
}

function trackEmailClientInstructionsClicked(emailClient: AnalyticsSignatureEmailClientType): void {
  analytics.track({
    name: 'onboarding_signature_clientinstruction_clicked',
    properties: {
      email_client_type: emailClient,
    },
  });
}

function generateLinkComesFromEmailSignature(originalLink: string): URL {
  const newLink = new URL(originalLink, window.location.origin);
  newLink.searchParams.set('fromEmailSignature', 'true');

  return newLink;
}

export {
  getMemorisedOnboardingEmailSignatureId,
  composeExampleEmailSignature,
  memoriseOnboardingEmailSignatureId,
  createNewUserFromEmailSignature,
  createNewEmailSignature,
  updateEmailSignature,
  updateUserFromEmailSignature,
  updateUserDetailsUponSigningUp,
  getGeo,
  resetWhenSomethingGoneWrong,
  trackEmailClientInstructionsClicked,
  generateLinkComesFromEmailSignature,
  memoriesOnboardingSignupUserId,
  getMemorisedOnboardingSignupUserId,
  memoriesOnboardingSignupSignatureId,
  getMemorisedOnboardingSignupSignatureId,
};
